import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { settingsAPI } from '@/lib/api';
import DepartmentsManager from '@/components/settings/DepartmentsManager';
import { toast } from 'sonner';
import {
  Settings,
  Loader2,
  Plus,
  X,
  Save,
  Calculator,
  Info,
} from 'lucide-react';

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
      setStatuses(response.data.employee_statuses || ['active', 'inactive', 'on_leave', 'terminated']);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update({
        employee_statuses: statuses,
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addStatus = () => {
    if (newStatus.trim() && !statuses.includes(newStatus.trim().toLowerCase().replace(/\s+/g, '_'))) {
      setStatuses([...statuses, newStatus.trim().toLowerCase().replace(/\s+/g, '_')]);
      setNewStatus('');
    }
  };

  const removeStatus = (statusToRemove) => {
    if (statuses.length > 1) {
      setStatuses(statuses.filter(s => s !== statusToRemove));
    } else {
      toast.error('At least one status is required');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 spinner text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="settings-page" className="space-y-6 max-w-2xl">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-1">
            Configure system-wide settings
          </p>
        </div>

        {/* Salary Calculation Info */}
        <Card className="border-blue-200 dark:border-blue-900 bg-blue-50/50 dark:bg-blue-950/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
              <Calculator className="h-5 w-5" />
              Salary Calculation
            </CardTitle>
            <CardDescription>
              Understanding how salary is calculated
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-white dark:bg-slate-900 rounded-lg">
              <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="space-y-2 text-sm">
                <p className="font-medium">Salary Divisor: Fixed at 30 days</p>
                <p className="text-muted-foreground">
                  The salary calculation uses a fixed 30-day month divisor for consistency across all calculations.
                </p>
              </div>
            </div>

            <div className="p-4 bg-white dark:bg-slate-900 rounded-lg">
              <p className="text-sm font-medium mb-3">Calculation Formula:</p>
              <div className="space-y-2 text-sm text-muted-foreground font-mono">
                <p>Net Eligible Salary = Gross − Retention</p>
                <p>Per Day Salary = Net Eligible / 30</p>
                <p>LOP Deduction = Per Day × LOP Days</p>
                <p>Employee EPF = ₹1,800 <span className="text-[11px] not-italic">(only when EPF is enabled on the organization)</span></p>
                <p className="font-semibold text-foreground pt-2 border-t">
                  Final Payable = Net Eligible − LOP Deduction + Rewards − Employee EPF
                </p>
              </div>
              <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                EPF is a flat <strong>₹1,800/month</strong> employee deduction (with a matching ₹1,800 employer contribution tracked for compliance/EPF reports). Toggle <em>EPF Enabled</em> on the organization to activate it.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div className="p-4 bg-white dark:bg-slate-900 rounded-lg">
                <p className="text-sm font-medium mb-2">Example · EPF Off</p>
                <div className="space-y-1 text-sm text-muted-foreground font-mono">
                  <p>Gross Salary: ₹50,000</p>
                  <p>Retention: ₹5,000</p>
                  <p>Net Eligible: ₹45,000</p>
                  <p>Per Day: ₹45,000 ÷ 30 = ₹1,500</p>
                  <p>LOP Days: 2</p>
                  <p>LOP Deduction: ₹3,000</p>
                  <p>Employee EPF: ₹0</p>
                  <p className="font-semibold text-foreground pt-2 border-t">
                    Final Payable: ₹42,000
                  </p>
                </div>
              </div>

              <div className="p-4 bg-white dark:bg-slate-900 rounded-lg border-emerald-200 dark:border-emerald-900 border">
                <p className="text-sm font-medium mb-2 flex items-center justify-between">
                  <span>Example · EPF On <span className="text-[11px] text-muted-foreground">(e.g. Blogic Tech)</span></span>
                  <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200 px-1.5 py-0.5 rounded">EPF</span>
                </p>
                <div className="space-y-1 text-sm text-muted-foreground font-mono">
                  <p>Gross Salary: ₹50,000</p>
                  <p>Retention: ₹5,000</p>
                  <p>Net Eligible: ₹45,000</p>
                  <p>Per Day: ₹45,000 ÷ 30 = ₹1,500</p>
                  <p>LOP Days: 2</p>
                  <p>LOP Deduction: ₹3,000</p>
                  <p>Employee EPF: <span className="text-rose-600">−₹1,800</span></p>
                  <p className="font-semibold text-foreground pt-2 border-t">
                    Final Payable: ₹40,200
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Employee Statuses */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Employee Statuses
            </CardTitle>
            <CardDescription>
              Configure available statuses for employees
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {statuses.map((status) => (
                <Badge
                  key={status}
                  variant="secondary"
                  className="px-3 py-1.5 text-sm flex items-center gap-2"
                  data-testid={`status-badge-${status}`}
                >
                  {status.replace('_', ' ')}
                  <button
                    onClick={() => removeStatus(status)}
                    className="hover:text-destructive transition-colors"
                    data-testid={`remove-status-${status}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>

            <div className="flex gap-2">
              <Input
                placeholder="Add new status..."
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addStatus())}
                className="max-w-[200px]"
                data-testid="new-status-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={addStatus}
                disabled={!newStatus.trim()}
                data-testid="add-status-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              These statuses will be available when creating or editing employees.
            </p>
          </CardContent>
        </Card>

        {/* Departments */}
        <DepartmentsManager />

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 spinner" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
